import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import {
  getAnalyticsLogsApi,
  getAnalyticsSummaryApi,
  exportAnalyticsLogsApi,
  getAnalyticsLogByIdApi,
  getUniqueUsersApi,
  getUserAnalyticsApi,
  getUniqueDevicesApi,
  getDeviceAnalyticsApi,
} from "../../services/analytics";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import Pagination from "react-js-pagination";
import { toastMessage } from "../../utils/toastMessage";
import { dateFormatUtc, dateFormat } from "../../utils/dateFormat";
import { DatePicker, Select } from "../../components/Form";
import {
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SecondaryButton } from "../../components/Button/Button";
import IconButtonComponent from "../../components/Button/IconButton";
import scrollToUp from "../../utils/scrollToUp";
import Loader from "../../comman/Loader";
import NotFound from "../../components/not-found/NotFound";
import findSerialNumber from "../../utils/findSerialNumber";
import common from "../../routes/common";
import csv from "../../Assests/image/csv.svg";
import useDownloadWithPassword from "../../hooks/useDownloadWithPassword";

const { NOT_FOUND } = common;

const AnalyticsManagement = () => {
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loading: exportLoading, requestDownloadWithCallback, DownloadPasswordModal } = useDownloadWithPassword();
  
  // Pagination
  const [activePage, setActivePage] = useState(
    +searchParams.get("page") || 1
  );
  const [pageLimit, setPageLimit] = useState(
    +searchParams.get("limit") || 10
  );
  
  // Search
  const [search, setSearch] = useState("");
  
  // Filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [eventNameFilter, setEventNameFilter] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("");
  
  // Time range filter states
  const [timeRange, setTimeRange] = useState("");
  const [timeRangeStartDate, setTimeRangeStartDate] = useState("");
  const [timeRangeEndDate, setTimeRangeEndDate] = useState("");
  
  // Data states
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Detail modal
  const [detailModal, setDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Device detail modal
  const [deviceDetailModal, setDeviceDetailModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // User detail modal
  const [userDetailModal, setUserDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0); // 0: All Logs, 1: With UserId, 2: Anonymous

  // User ID tab states
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [userLogsTotal, setUserLogsTotal] = useState(0);
  const [userLogsPage, setUserLogsPage] = useState(1);
  const [loadingUserLogs, setLoadingUserLogs] = useState(false);

  // Anonymous tab states (device-based)
  const [uniqueDevices, setUniqueDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [deviceLogs, setDeviceLogs] = useState([]);
  const [deviceLogsTotal, setDeviceLogsTotal] = useState(0);
  const [deviceLogsPage, setDeviceLogsPage] = useState(1);
  const [loadingDeviceLogs, setLoadingDeviceLogs] = useState(false);

  // Page limit options
  const pageLimitOptions = [
    { value: 10, label: "10 per page" },
    { value: 20, label: "20 per page" },
    { value: 25, label: "25 per page" },
    { value: 50, label: "50 per page" },
    { value: 100, label: "100 per page" },
  ];

  // Time range options
  const timeRangeOptions = [
    { value: "", label: "All Time Ranges" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last7days", label: "Last 7 Days" },
    { value: "last30days", label: "Last 30 Days" },
    { value: "thisMonth", label: "This Month" },
    { value: "lastMonth", label: "Last Month" },
    { value: "custom", label: "Custom Range" },
  ];

  // Source options
  const sourceOptions = [
    { value: "", label: "All Sources" },
    { value: "Whatsapp", label: "Whatsapp" },
    { value: "Website Organic", label: "Website Organic" },
    { value: "Social Media Organic", label: "Social Media Organic" },
    { value: "Social Media Paid", label: "Social Media Paid" },
    { value: "Website Paid", label: "Website Paid" },
    { value: "direct", label: "Direct" },
  ];

  // Event type options
  const eventTypeOptions = [
    { value: "", label: "All Event Types" },
    { value: "user_registration", label: "User Registration" },
    { value: "user_login", label: "User Login" },
    { value: "screen_view", label: "Screen View" },
    { value: "purchase", label: "Purchase" },
    { value: "app_launch", label: "App Launch" },
    { value: "app_install", label: "App Install" },
    { value: "app_uninstall", label: "App Uninstall" },
    { value: "deep_link", label: "Deep Link" },
    { value: "user_action", label: "User Action" },
    { value: "other", label: "Other" },
  ];

  // Platform options
  const platformOptions = [
    { value: "", label: "All Platforms" },
    { value: "mobile", label: "Mobile" },
    { value: "web", label: "Web" },
    { value: "admin", label: "Admin" },
  ];

  // Device type options
  const deviceTypeOptions = [
    { value: "", label: "All Device Types" },
    { value: "ios", label: "iOS" },
    { value: "android", label: "Android" },
    { value: "web", label: "Web" },
    { value: "unknown", label: "Unknown" },
  ];

  // Fetch analytics logs
  // const fetchLogs = async (page = 1) => {
  //   setLoading(true);
  //   try {
  //     const params = {
  //       page: page,
  //       limit: pageLimit,
  //       timeRange: timeRange || undefined,
  //       timeRangeStartDate: timeRange === "custom" && timeRangeStartDate ? timeRangeStartDate : undefined,
  //       timeRangeEndDate: timeRange === "custom" && timeRangeEndDate ? timeRangeEndDate : undefined,
  //       start_date: timeRange === "custom" && startDate ? dateFormatUtc(startDate) : undefined,
  //       end_date: timeRange === "custom" && endDate ? dateFormatUtc(endDate) : undefined,
  //       source: sourceFilter || undefined,
  //       event_type: eventTypeFilter || undefined,
  //       platform: platformFilter || undefined,
  //       event_name: eventNameFilter || undefined,
  //       user_id: userIdFilter || undefined,
  //       device_type: deviceTypeFilter || undefined,
  //       // Filter based on active tab
  //       has_user_id: activeTab === 1 ? true : activeTab === 2 ? false : undefined,
  //     };

  //     const response = await getAnalyticsLogsApi(params);
  //     if (response.success) {
  //       setLogs(response.data.logs || []);
  //       setTotal(response.data.pagination?.total || 0);
  //     } else {
  //       toastMessage("error", response.message || "Failed to fetch logs");
  //     }
  //   } catch (error) {
  //     toastMessage("error", error.message || "Failed to fetch analytics logs");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Fetch unique users (for userId tab)
  const fetchUniqueUsers = async () => {
    setLoadingUsers(true);
    try {
      const params = {
        timeRange: timeRange || undefined,
        timeRangeStartDate: timeRange === "custom" && timeRangeStartDate ? timeRangeStartDate : undefined,
        timeRangeEndDate: timeRange === "custom" && timeRangeEndDate ? timeRangeEndDate : undefined,
        start_date: timeRange === "custom" && startDate ? dateFormatUtc(startDate) : undefined,
        end_date: timeRange === "custom" && endDate ? dateFormatUtc(endDate) : undefined,
        source: sourceFilter || undefined,
        event_type: eventTypeFilter || undefined,
        platform: platformFilter || undefined,
        device_type: deviceTypeFilter || undefined,
      };

      const response = await getUniqueUsersApi(params);
      if (response.success) {
        setUniqueUsers(response.data.users || []);
      } else {
        toastMessage("error", response.message || "Failed to fetch unique users");
      }
    } catch (error) {
      toastMessage("error", error.message || "Failed to fetch unique users");
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch logs for selected user
  const fetchUserLogs = async (userId, page = 1) => {
    setLoadingUserLogs(true);
    try {
      const params = {
        page: page,
        limit: pageLimit,
        timeRange: timeRange || undefined,
        timeRangeStartDate: timeRange === "custom" && timeRangeStartDate ? timeRangeStartDate : undefined,
        timeRangeEndDate: timeRange === "custom" && timeRangeEndDate ? timeRangeEndDate : undefined,
        start_date: timeRange === "custom" && startDate ? dateFormatUtc(startDate) : undefined,
        end_date: timeRange === "custom" && endDate ? dateFormatUtc(endDate) : undefined,
      };

      const response = await getUserAnalyticsApi(userId, params);
      if (response.success) {
        setUserLogs(response.data.logs || []);
        setUserLogsTotal(response.data.pagination?.total || 0);
        setUserLogsPage(page);
      } else {
        toastMessage("error", response.message || "Failed to fetch user logs");
      }
    } catch (error) {
      toastMessage("error", error.message || "Failed to fetch user logs");
    } finally {
      setLoadingUserLogs(false);
    }
  };

  // Handle user selection - navigate to user analytics page
  const handleUserSelect = (userId) => {
    navigate(`/analytics-management/user/${userId}`);
  };

  // Fetch unique devices (for anonymous tab)
  const fetchUniqueDevices = async () => {
    setLoadingDevices(true);
    try {
      const params = {
        timeRange: timeRange || undefined,
        timeRangeStartDate: timeRange === "custom" && timeRangeStartDate ? timeRangeStartDate : undefined,
        timeRangeEndDate: timeRange === "custom" && timeRangeEndDate ? timeRangeEndDate : undefined,
        start_date: timeRange === "custom" && startDate ? dateFormatUtc(startDate) : undefined,
        end_date: timeRange === "custom" && endDate ? dateFormatUtc(endDate) : undefined,
        source: sourceFilter || undefined,
        event_type: eventTypeFilter || undefined,
        platform: platformFilter || undefined,
        device_type: deviceTypeFilter || undefined,
      };

      const response = await getUniqueDevicesApi(params);
      if (response.success) {
        setUniqueDevices(response.data.devices || []);
      } else {
        toastMessage("error", response.message || "Failed to fetch unique devices");
      }
    } catch (error) {
      toastMessage("error", error.message || "Failed to fetch unique devices");
    } finally {
      setLoadingDevices(false);
    }
  };

  // Fetch logs for selected device
  const fetchDeviceLogs = async (deviceId, page = 1) => {
    setLoadingDeviceLogs(true);
    try {
      const params = {
        page: page,
        limit: pageLimit,
        timeRange: timeRange || undefined,
        timeRangeStartDate: timeRange === "custom" && timeRangeStartDate ? timeRangeStartDate : undefined,
        timeRangeEndDate: timeRange === "custom" && timeRangeEndDate ? timeRangeEndDate : undefined,
        start_date: timeRange === "custom" && startDate ? dateFormatUtc(startDate) : undefined,
        end_date: timeRange === "custom" && endDate ? dateFormatUtc(endDate) : undefined,
      };

      const response = await getDeviceAnalyticsApi(deviceId, params);
      if (response.success) {
        setDeviceLogs(response.data.logs || []);
        setDeviceLogsTotal(response.data.pagination?.total || 0);
        setDeviceLogsPage(page);
      } else {
        toastMessage("error", response.message || "Failed to fetch device logs");
      }
    } catch (error) {
      toastMessage("error", error.message || "Failed to fetch device logs");
    } finally {
      setLoadingDeviceLogs(false);
    }
  };

  // Handle device selection - navigate to device analytics page
  const handleDeviceSelect = (deviceId) => {
    navigate(`/analytics-management/device/${deviceId}`);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedUserId(null);
    setUserLogs([]);
    setUserLogsTotal(0);
    setUserLogsPage(1);
    setSelectedDeviceId(null);
    setDeviceLogs([]);
    setDeviceLogsTotal(0);
    setDeviceLogsPage(1);
    setActivePage(1);
    
    if (newValue === 0) {
      // Load regular logs for all logs tab
      fetchUniqueDevices();
    } else if (newValue === 1) {
      // Load unique users when switching to userId tab
      fetchUniqueUsers();
    }
  };

  // Fetch summary
  const fetchSummary = async () => {
    try {
      const params = {
        timeRange: timeRange || undefined,
        timeRangeStartDate: timeRange === "custom" && timeRangeStartDate ? timeRangeStartDate : undefined,
        timeRangeEndDate: timeRange === "custom" && timeRangeEndDate ? timeRangeEndDate : undefined,
        start_date: timeRange === "custom" && startDate ? dateFormatUtc(startDate) : undefined,
        end_date: timeRange === "custom" && endDate ? dateFormatUtc(endDate) : undefined,
        source: sourceFilter || undefined,
        event_type: eventTypeFilter || undefined,
        platform: platformFilter || undefined,
      };

      const response = await getAnalyticsSummaryApi(params);
      if (response.success) {
        setSummary(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setActivePage(page);
    setSearchParams({ page, limit: pageLimit });
    if (activeTab === 0) {
    fetchUniqueDevices(page);
    }
  };

  // Handle page limit change
  const handlePageLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPageLimit(newLimit);
    setActivePage(1);
  };

  // Apply filters
  const applyFilters = () => {
    setActivePage(1);
    setSearchParams({ page: 1, limit: pageLimit });
    if (activeTab === 1) {
      if (selectedUserId) {
        fetchUserLogs(selectedUserId, 1);
      } else {
        fetchUniqueUsers();
      }
    } else {
    fetchUniqueDevices(1);
    fetchSummary();
    }
  };

  // Time range handler
  const handleTimeRangeChange = (value) => {
    setTimeRange(value);
    if (value !== "custom") {
      setTimeRangeStartDate("");
      setTimeRangeEndDate("");
      setStartDate(null);
      setEndDate(null);
    }
  };

  // Time range date change handler
  const handleTimeRangeDateChange = (type, value) => {
    if (type === "start") {
      setTimeRangeStartDate(value);
      // Also update startDate for custom range
      if (value) {
        setStartDate(new Date(value));
      }
    } else {
      setTimeRangeEndDate(value);
      // Also update endDate for custom range
      if (value) {
        setEndDate(new Date(value));
      }
    }
  };

  // Refresh filters
  const refreshFilters = () => {
    setSearch("");
    setStartDate(null);
    setEndDate(null);
    setSourceFilter("");
    setEventTypeFilter("");
    setPlatformFilter("");
    setEventNameFilter("");
    setUserIdFilter("");
    setDeviceTypeFilter("");
    setTimeRange("");
    setTimeRangeStartDate("");
    setTimeRangeEndDate("");
    setActivePage(1);
    setPageLimit(10);
    setSearchParams({ page: 1, limit: 10 });
    if (activeTab === 0) {
      fetchUniqueDevices();

    } else if (activeTab === 1) {
      fetchUniqueUsers();
    } 
   
  };

  // Export logs
  const handleExport = () => {
    const params = {
      timeRange: timeRange || undefined,
      timeRangeStartDate: timeRange === "custom" && timeRangeStartDate ? timeRangeStartDate : undefined,
      timeRangeEndDate: timeRange === "custom" && timeRangeEndDate ? timeRangeEndDate : undefined,
      start_date: timeRange === "custom" && startDate ? dateFormatUtc(startDate) : undefined,
      end_date: timeRange === "custom" && endDate ? dateFormatUtc(endDate) : undefined,
      source: sourceFilter || undefined,
      event_type: eventTypeFilter || undefined,
      platform: platformFilter || undefined,
    };
    requestDownloadWithCallback(async () => {
      try {
        const blob = await exportAnalyticsLogsApi(params);
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `analytics-logs-${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toastMessage("success", "Analytics logs exported successfully");
      } catch (error) {
        toastMessage("error", "Failed to export analytics logs");
      }
    }, 'Analytics Management');
  };

  // View log details
  const handleViewDetails = async (logId) => {
    setLoadingDetail(true);
    setDetailModal(true);
    try {
      const response = await getAnalyticsLogByIdApi(logId);
      if (response.success) {
        setSelectedLog(response.data.log);
      } else {
        toastMessage("error", response.message || "Failed to fetch log details");
        setDetailModal(false);
      }
    } catch (error) {
      toastMessage("error", error.message || "Failed to fetch log details");
      setDetailModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Close detail modal
  const closeDetailModal = () => {
    setDetailModal(false);
    setSelectedLog(null);
  };

  useEffect(() => {
    scrollToUp();
    if (activeTab === 0) {
      // All logs tab
    fetchUniqueDevices(activePage);
    } else if (activeTab === 1) {
      // UserId tab - load unique users
      fetchUniqueUsers(activePage);
    }
  }, [activePage, pageLimit, activeTab]);

  // Chart data for source distribution
  const sourceChartData = summary?.source_distribution
    ? {
        series: summary.source_distribution.map((item) => item.count || 0),
        options: {
          chart: {
            type: "donut",
          },
          labels: summary.source_distribution.map((item) => item.source || "Unknown"),
          title: {
            text: "Source Distribution",
          },
          legend: {
            position: "bottom",
          },
        },
      }
    : null;

  // Chart data for events by type
  const eventTypeChartData = summary?.events_by_type
    ? {
        series: [
          {
            name: "Events",
            data: summary.events_by_type.map((item) => item.count || 0),
          },
        ],
        options: {
          chart: {
            type: "bar",
            height: 350,
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: "55%",
            },
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            categories: summary.events_by_type.map(
              (item) => item.event_type || "Unknown"
            ),
          },
          title: {
            text: "Events by Type",
          },
        },
      }
    : null;

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <div className="user_detail_heading">
              <h4 className="Title">Analytics Management</h4>
            </div>
            <div className="block_user_button">
              <SecondaryButton
                onClick={handleExport}
                disabled={exportLoading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {exportLoading ? (
                  <Loader />
                ) : (
                  <>
                    <div
                      className="csv-image-wrapper"
                      style={{ width: "16px", height: "16px" }}
                    >
                      <img
                        src={csv}
                        alt="csv file download"
                        style={{ width: "100%", height: "100%" }}
                      />
                    </div>
                    Export CSV
                  </>
                )}
              </SecondaryButton>
            </div>
          </div>

          {/* Summary Cards - Only show for All Logs and Anonymous tabs */}
          {/* {summary && (activeTab === 0 || activeTab === 2) && (
            <div className="mb-4" style={{ marginTop: "20px" }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Total Events
                      </Typography>
                      <Typography variant="h4">
                        {summary.summary?.total_events || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Unique Users
                      </Typography>
                      <Typography variant="h4">
                        {summary.summary?.unique_users || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Sources Tracked
                      </Typography>
                      <Typography variant="h4">
                        {summary.source_distribution?.length || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Event Types
                      </Typography>
                      <Typography variant="h4">
                        {summary.events_by_type?.length || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </div>
          )}

          {summary && (activeTab === 0 || activeTab === 2) && (
            <div className="mb-4">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {sourceChartData && (
                    <Card>
                      <CardContent>
                        <ReactApexChart
                          options={sourceChartData.options}
                          series={sourceChartData.series}
                          type="donut"
                          height={350}
                        />
                      </CardContent>
                    </Card>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {eventTypeChartData && (
                    <Card>
                      <CardContent>
                        <ReactApexChart
                          options={eventTypeChartData.options}
                          series={eventTypeChartData.series}
                          type="bar"
                          height={350}
                        />
                      </CardContent>
                    </Card>
                  )}
                </Grid>
              </Grid>
            </div>
          )} */}

          {/* Filters */}
          <div className="FilterBox FilterArea" style={{ flexDirection: "column" }}>
            {/* Row 1: Search and Basic Filters */}
            <div
              className="group_filter_value"
              style={{
                marginBottom: "15px",
                display: "flex !important",
                flexWrap: "wrap !important",
                gap: "15px",
                alignItems: "center",
                width: "100%",
                flexDirection: "row",
              }}
            >
              {/* Search Bar */}
              <div
                className="form-group search_md_size userSearch"
                style={{ minWidth: "200px", flex: "1 1 auto" }}
              >
                <input
                  type="search"
                  className={`form-control ${search && "active"}`}
                  placeholder="Search by Event Name or User ID"
                  value={search || ""}
                  style={{ "--color": "#FF8989" }}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      setEventNameFilter(search);
                      setUserIdFilter(search);
                      applyFilters();
                    }
                  }}
                />
              </div>

              {/* Source Filter */}
              <div
                className="form-group select_sm_width"
                style={{ minWidth: "150px", flex: "0 0 auto" }}
              >
                <Select
                  className={`form-control ${sourceFilter && "active"}`}
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  <option value="">All Sources</option>
                  {sourceOptions
                    .filter((opt) => opt.value !== "")
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </Select>
              </div>

              {/* Event Type Filter */}
              <div
                className="form-group select_sm_width"
                style={{ minWidth: "150px", flex: "0 0 auto" }}
              >
                <Select
                  className={`form-control ${eventTypeFilter && "active"}`}
                  value={eventTypeFilter}
                  onChange={(e) => setEventTypeFilter(e.target.value)}
                >
                  <option value="">All Event Types</option>
                  {eventTypeOptions
                    .filter((opt) => opt.value !== "")
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </Select>
              </div>

              {/* Platform Filter */}
              {/* <div
                className="form-group select_sm_width"
                style={{ minWidth: "150px", flex: "0 0 auto" }}
              >
                <Select
                  className={`form-control ${platformFilter && "active"}`}
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                >
                  <option value="">All Platforms</option>
                  {platformOptions
                    .filter((opt) => opt.value !== "")
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </Select>
              </div> */}

              {/* Device Type Filter */}
              <div
                className="form-group select_sm_width"
                style={{ minWidth: "150px", flex: "0 0 auto" }}
              >
                <Select
                  className={`form-control ${deviceTypeFilter && "active"}`}
                  value={deviceTypeFilter}
                  onChange={(e) => setDeviceTypeFilter(e.target.value)}
                >
                  <option value="">All Device Types</option>
                  {deviceTypeOptions
                    .filter((opt) => opt.value !== "")
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </Select>
              </div>
           
              {/* Time Range Filter */}
              <div
                className="form-group select_sm_width"
                style={{ minWidth: "150px", flex: "0 0 auto" }}
              >
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Time Range</InputLabel>
                  <MuiSelect
                    value={timeRange}
                    onChange={(e) => handleTimeRangeChange(e.target.value)}
                    label="Time Range"
                  >
                    {timeRangeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
              </div>

              {/* Custom Date Range - Show only when custom is selected */}
              {timeRange === "custom" && (
                <>
                  <div
                    className="form-group select_sm_width"
                    style={{ minWidth: "150px", flex: "0 0 auto" }}
                  >
                    <TextField
                      size="small"
                      type="date"
                      variant="outlined"
                      label="Start Date"
                      value={timeRangeStartDate}
                      onChange={(e) =>
                        handleTimeRangeDateChange("start", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </div>
                  <div
                    className="form-group select_sm_width"
                    style={{ minWidth: "150px", flex: "0 0 auto" }}
                  >
                    <TextField
                      size="small"
                      type="date"
                      variant="outlined"
                      label="End Date"
                      value={timeRangeEndDate}
                      onChange={(e) =>
                        handleTimeRangeDateChange("end", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </div>
                </>
              )}

              {/* Apply & Clear Buttons */}
              <div
                className="form-group refresh_button"
                style={{
                  minWidth: "120px",
                  flex: "0 0 auto",
                  display: "flex",
                  gap: "10px",
                }}
              >
                <SecondaryButton onClick={applyFilters}>
                  Apply
                </SecondaryButton>
                <SecondaryButton onClick={refreshFilters}>
                  Clear
                </SecondaryButton>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, mt: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="analytics tabs">
              {/* <Tab label="All Logs" /> */}
              <Tab label="Anonymous Users" />
              <Tab label="Authenticated Users" />
            </Tabs>
          </Box>
           {/* Anonymous Tab Content - Unique Devices List */}
           {activeTab === 0 && (
            <div className="content-table">
              <div style={{ marginBottom: "20px" }}>
                <Typography variant="h6" gutterBottom>
                  Unique Devices ({uniqueDevices.length})
                </Typography>
              </div>
              {loadingDevices ? (
                <div className="text-center py-5">
                  <Loader />
                </div>
              ) : uniqueDevices.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>S. No.</th>
                      <th>Device ID</th>
                      <th>Device Type</th>
                      <th>Device Model</th>
                      <th>App Version</th>
                      <th>Total Events</th>
                      <th>Sources</th>
                      <th>First Event</th>
                      <th>Last Event</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueDevices.map((device, index) => (
                      <tr key={device.device_id || index}>
                        <td>{index + 1}</td>
                        <td style={{ wordBreak: "break-all", maxWidth: "200px" }}>
                          {device.device_id || "---"}
                        </td>
                        <td>
                          {device.device_type ? (
                            <span
                              className="badge"
                              style={{
                                backgroundColor: device.device_type === "ios" ? "#007AFF" : device.device_type === "android" ? "#3DDC84" : "#999",
                                color: "white",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                textTransform: "uppercase",
                              }}
                            >
                              {device.device_type}
                            </span>
                          ) : (
                            "---"
                          )}
                        </td>
                        <td>{device.device_model || "---"}</td>
                        <td>{device.app_version || "---"}</td>
                        <td>{device.total_events || 0}</td>
                       
                        <td>
                          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                            {device.sources?.slice(0, 2).map((source, i) => (
                              <span
                                key={i}
                                className="badge"
                                style={{
                                  backgroundColor: "#FF8989",
                                  color: "white",
                                  padding: "2px 6px",
                                  borderRadius: "3px",
                                  fontSize: "10px",
                                }}
                              >
                                {source}
                              </span>
                            ))}
                            {device.sources?.length > 2 && (
                              <span style={{ fontSize: "10px", color: "#666" }}>
                                +{device.sources.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {device.first_event
                            ? dateFormat(device.first_event)
                            : "---"}
                        </td>
                        <td>
                          {device.last_event
                            ? dateFormat(device.last_event)
                            : "---"}
                        </td>
                        <td>
                        <Link to={`/analytics-management/device/${device.device_id}`}>
                                <IconButton
                                  aria-label="update"
                                  className="px-2 py-0"
                                >
                                  <VisibilityIcon className="eye_icon" />
                                </IconButton>
                              </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-5">
                  <NotFound message="No unique devices found!" />
                </div>
              )}
            </div>
          )}

          {/* User ID Tab Content - Unique Users List */}
          {activeTab === 1  && (
            <div className="content-table">
              <div style={{ marginBottom: "20px" }}>
                <Typography variant="h6" gutterBottom>
                  Unique Users ({uniqueUsers.length})
                </Typography>
              </div>
              {loadingUsers ? (
                <div className="text-center py-5">
                  <Loader />
                </div>
              ) : uniqueUsers.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>S. No.</th>
                      <th>User ID</th>
                      <th>Total Events</th>
                      <th>Sources</th>
                      <th>First Event</th>
                      <th>Last Event</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueUsers.map((user, index) => (
                      <tr key={user.user_id || index}>
                        <td>{index + 1}</td>
                        <td style={{ wordBreak: "break-all", maxWidth: "200px" }}>
                          {user.user_id || "---"}
                        </td>
                        <td>{user.total_events || 0}</td>
                      
                        <td>
                          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                            {user.sources?.slice(0, 2).map((source, i) => (
                              <span
                                key={i}
                                className="badge"
                                style={{
                                  backgroundColor: "#FF8989",
                                  color: "white",
                                  padding: "2px 6px",
                                  borderRadius: "3px",
                                  fontSize: "10px",
                                }}
                              >
                                {source}
                              </span>
                            ))}
                            {user.sources?.length > 2 && (
                              <span style={{ fontSize: "10px", color: "#666" }}>
                                +{user.sources.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {user.first_event
                            ? dateFormat(user.first_event)
                            : "---"}
                        </td>
                        <td>
                          {user.last_event
                            ? dateFormat(user.last_event)
                            : "---"}
                        </td>
                        <td>
                        <Link to={`/analytics-management/user/${user.user_id}`}>
                                <IconButton
                                  aria-label="update"
                                  className="px-2 py-0"
                                >
                                  <VisibilityIcon className="eye_icon" />
                                </IconButton>
                              </Link>

                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-5">
                  <NotFound message="No unique users found!" />
                </div>
              )}
            </div>
          )}


        </div>
      </div>

    
      <DownloadPasswordModal />
    </>
  );
};

export default AnalyticsManagement;
